import { useCallback, useEffect, useState } from 'react';
import { useT } from '@dia/i18n';
import { Button, LangToggle, Screen, StatusPill } from '@dia/ui';
import { BackLink } from './parts/BackLink';
import { cacheAge, needsLightningNote, type ForecastDay } from './logic/weatherCache';
import { fetchForecast, readCachedForecast, type CachedForecast } from './logic/weatherStore';

/**
 * Weather for the island, from Open-Meteo, cached in Dexie.
 *
 * The screen never shows a blank and never pretends: with no signal it shows
 * the last forecast it saved and says how old it is, and it is honest that the
 * reading is old once it passes three hours. Phase 3 replaces this with the
 * island's own station.
 */
export function Weather() {
  const { t, lang } = useT();
  const [data, setData] = useState<CachedForecast | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [offline, setOffline] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const refresh = useCallback(async () => {
    setFetching(true);
    const fresh = await fetchForecast();
    if (fresh) {
      setData(fresh);
      setOffline(false);
    } else {
      setOffline(true);
    }
    setNow(Date.now());
    setFetching(false);
  }, []);

  useEffect(() => {
    let live = true;
    (async () => {
      const cached = await readCachedForecast();
      if (live) {
        if (cached) setData(cached);
        setLoaded(true);
      }
      await refresh();
    })();
    const onOnline = () => void refresh();
    window.addEventListener('online', onOnline);
    const tick = setInterval(() => setNow(Date.now()), 60_000);
    return () => {
      live = false;
      window.removeEventListener('online', onOnline);
      clearInterval(tick);
    };
  }, [refresh]);

  const age = data ? cacheAge(data.fetchedAt, now) : null;
  const ageLabel = age
    ? age.unit === 'now'
      ? t('guest.weather.updated_now')
      : t(`guest.weather.updated_${age.unit}`, { count: age.value })
    : '';

  const dayName = (day: ForecastDay, index: number) =>
    index === 0
      ? t('guest.weather.today')
      : new Intl.DateTimeFormat(lang, { weekday: 'long', timeZone: 'UTC' }).format(new Date(`${day.date}T12:00:00Z`));

  const round = (n: number | null) => (n === null ? '-' : Math.round(n));
  const toF = (c: number | null) => (c === null ? '-' : Math.round((c * 9) / 5 + 32));

  return (
    <Screen title={t('guest.weather.title')} lede={t('guest.weather.lede')} actions={<LangToggle />}>
      <BackLink />
      <div className="flex flex-col gap-8 mt-2">
        {loaded && !data && (
          <p className="text-inkMuted" role="status">
            {t('guest.weather.none')}
          </p>
        )}

        {data && age && (
          <>
            <div className="flex flex-wrap items-center gap-2" role="status">
              <StatusPill tone={age.stale ? 'warn' : 'ok'} glyph={age.stale ? '!' : 'OK'}>
                {ageLabel}
              </StatusPill>
              {offline && <span className="text-inkFaint text-caption">{t('guest.weather.offline')}</span>}
              {age.stale && !offline && <span className="text-inkFaint text-caption">{t('guest.weather.stale')}</span>}
            </div>

            <section aria-labelledby="now" className="flex flex-col gap-2">
              <h2 id="now" className="font-display text-[1.25em]">
                {t('guest.weather.now')}
              </h2>
              <p className="font-display text-[2.4em] leading-none text-ink">
                {t('guest.weather.temp', {
                  temp: round(data.forecast.now.tempC),
                  tempF: toF(data.forecast.now.tempC),
                })}
              </p>
              <p className="text-ink text-[1.15em]">{t(`guest.weather.kind.${data.forecast.now.kind}`)}</p>
              <p className="text-inkMuted">
                {data.forecast.now.humidityPct !== null &&
                  `${t('guest.weather.humidity', { pct: round(data.forecast.now.humidityPct) })}. `}
                {data.forecast.now.windKph !== null &&
                  `${t('guest.weather.wind', { kph: round(data.forecast.now.windKph) })}`}
                {data.forecast.now.gustKph !== null &&
                  `, ${t('guest.weather.gusts', { kph: round(data.forecast.now.gustKph) })}`}
              </p>
            </section>

            {needsLightningNote(data.forecast) && (
              <p className="border-l-2 border-mark pl-4 text-ink" role="note">
                {t('guest.weather.thunder')}
              </p>
            )}

            <section aria-labelledby="next" className="flex flex-col">
              <h2 id="next" className="font-display text-[1.25em] mb-1">
                {t('guest.weather.next_days')}
              </h2>
              <ul>
                {data.forecast.days.map((day, i) => (
                  <li key={day.date} className="border-b border-line last:border-b-0 py-3 flex flex-col gap-0.5">
                    <span className="font-display text-[1.15em] text-ink capitalize">{dayName(day, i)}</span>
                    <span className="text-ink">{t(`guest.weather.kind.${day.kind}`)}</span>
                    <span className="text-inkMuted text-caption">
                      {t('guest.weather.high_low', { high: round(day.highC), low: round(day.lowC) })}
                      {day.rainChancePct !== null &&
                        `. ${t('guest.weather.rain_chance', { pct: round(day.rainChancePct) })}`}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}

        <div className="flex flex-col gap-3">
          <Button variant="secondary" onClick={() => void refresh()} busy={fetching} busyLabel={t('loading')}>
            {t('guest.weather.refresh')}
          </Button>
          <p className="text-inkFaint text-caption">{t('guest.weather.source')}</p>
        </div>
      </div>
    </Screen>
  );
}
